import { injectable } from 'inversify';

import { FormatService } from '@vk-hr-tek/core/format';

import { Attorney } from '@app/gen/attorneys';
import { ATTORNEYS_POWERS_DEFAULT } from '@app/constants';

import { Attorney as ProcessedAttorney, ActionType } from '../../types';

@injectable()
export class AttorneyDetailMapper {
  constructor(private readonly format: FormatService) {}

  process(
    attorney: Attorney,
    options: { canSign: boolean },
  ): ProcessedAttorney {
    return {
      id: attorney.id,
      name: attorney.full_name,
      title: `МЧД: ${attorney.full_name}`,
      tooltip: this.processTooltip(attorney),
      validityPeriod: `Срок действия с ${this.format.toDate(
        attorney.valid_from,
      )} по ${this.format.toDate(attorney.valid_to)}`,
      snils: this.format.toSnils(attorney.snils),
      issuer: `Выпущена: ${attorney.issuer}`,
      createdAt: this.format.toDate(attorney.creation_date),
      cryptoType: attorney.company.crypto_pro_plugin_open_type,
      company: attorney.company,
      info: [
        { label: 'Юрлицо', value: attorney.company.name, canCopy: false },
        {
          label: 'Полномочия',
          value: attorney.powers || ATTORNEYS_POWERS_DEFAULT,
          canCopy: false,
        },
        {
          label: 'Причина отзыва',
          value: attorney.revocation_reason || '',
          canCopy: false,
        },
        { label: 'Автор', value: attorney.author_full_name, canCopy: false },
        {
          label: attorney.imported ? 'Дата импорта' : 'Дата создания',
          value: this.format.toDate(attorney.creation_date),
          canCopy: false,
        },
        { label: 'UID', value: attorney.id, canCopy: true },
      ],
      stage: this.processStages(attorney),
      actions: this.processActions(attorney, options),
    };
  }

  processActions(attorney: Attorney, { canSign = false } = {}) {
    const buttons: Record<
      ActionType,
      {
        color: 'warning' | 'primary';
        title: string;
      }
    > = {
      delete: {
        color: 'warning',
        title: 'Удалить',
      },
      signAttorney: {
        color: 'primary',
        title: 'Подписать',
      },
      cancel: {
        color: 'primary',
        title: 'Отключить',
      },
      export: {
        color: 'primary',
        title: '',
      },
      revoke: {
        color: 'primary',
        title: 'Отозвать',
      },
      unrevoke: {
        color: 'warning',
        title: 'Удалить отзыв',
      },
      signRevoke: {
        color: 'primary',
        title: 'Подписать отзыв',
      },
    };
    return this.processActionsTypes(attorney, { canSign }).map((action) => ({
      type: action,
      companyName: attorney.company.name,
      button: buttons[action],
      attorneys: [
        {
          id: attorney.id,
          name: attorney.full_name,
          validityPeriod: `${this.format.toDate(
            attorney.valid_from,
          )} - ${this.format.toDate(attorney.valid_to)}`,
          createdAt: this.format.toDate(attorney.creation_date),
          issuer: attorney.issuer,
        },
      ],
    }));
  }

  processActionsTypes(
    attorney: { status: Attorney['status']; imported: boolean },
    options: { canSign: boolean },
  ): ActionType[] {
    switch (attorney.status) {
      case 'new': {
        return options.canSign ? ['delete', 'signAttorney'] : ['delete'];
      }

      case 'signed': {
        return attorney.imported ? ['cancel', 'export'] : ['revoke', 'export'];
      }

      case 'canceled':
      case 'revoked':
      case 'expired': {
        return ['export'];
      }

      case 'revoking': {
        return options.canSign
          ? ['unrevoke', 'signRevoke', 'export']
          : ['unrevoke', 'export'];
      }

      /* istanbul ignore next */
      // should be unreachable
      default: {
        const label: never = attorney.status;

        return [label];
      }
    }
  }

  processStages(attorney: { status: Attorney['status'] }) {
    switch (attorney.status) {
      case 'new': {
        return {
          label: 'На подписании',
          color: 'blue' as const,
        };
      }

      case 'signed': {
        return {
          label: 'Действующая',
          color: 'green' as const,
        };
      }

      case 'expired': {
        return {
          label: 'Истекла',
          color: 'gray' as const,
        };
      }

      case 'revoking': {
        return {
          label: 'В процессе отзыва',
          color: 'orange' as const,
        };
      }

      case 'revoked': {
        return {
          label: 'Отозвана',
          color: 'red' as const,
        };
      }

      case 'canceled': {
        return {
          label: 'Отключена',
          color: 'purple' as const,
        };
      }

      /* istanbul ignore next */
      // should be unreachable
      default: {
        const label: never = attorney.status;

        return {
          label,
          color: 'gray' as const,
        };
      }
    }
  }

  processTooltip(attorney: { status: Attorney['status'] }) {
    if (attorney.status === 'new') {
      return 'Необходимо подписать';
    }

    if (attorney.status === 'revoking') {
      return 'Необходимо подписать отзыв';
    }

    return '';
  }
}
